import api from '../api'
import {
  getActiveAccount,
  getLoginRequest,
  getMsalInstance,
  isEntraConfigured,
} from '../../services/auth/entraClient'

const LOCAL_AUTH_STORAGE_KEY = 'platform-ui.local-auth-user'
const CLEAR_AUTH_ON_LOAD_KEY = 'platform-ui.clear-auth-on-load.applied'
const LAST_ACTIVITY_KEY = 'platform-ui.last-activity'
const FORCE_REAUTH_KEY = 'platform-ui.force-reauth'

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

export function updateLastActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
  } catch (error) {
    // ignore storage errors
  }
}

function getLastActivity() {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
    return raw ? parseInt(raw, 10) : 0
  } catch (error) {
    return 0
  }
}

function markForceReauth() {
  try {
    localStorage.setItem(FORCE_REAUTH_KEY, 'true')
  } catch (error) {
    // ignore storage errors
  }
}

function clearForceReauth() {
  try {
    localStorage.removeItem(FORCE_REAUTH_KEY)
  } catch (error) {
    // ignore storage errors
  }
}

function shouldForceReauth() {
  try {
    return localStorage.getItem(FORCE_REAUTH_KEY) === 'true'
  } catch (error) {
    return false
  }
}

function isSessionExpired() {
  const last = getLastActivity()
  if (!last) return false
  return Date.now() - last > IDLE_TIMEOUT_MS
}

function isFeatureEnabled(state, feature) {
  return state.features.featureFlags.includes(feature)
}

function isRolesEnabled(state) {
  return isFeatureEnabled(state, 'roles')
}

function persistUser(user) {
  try {
    localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify(user))
  } catch (error) {
    console.warn('Unable to persist auth user in local storage', error)
  }
}

function removePersistedUser() {
  try {
    localStorage.removeItem(LOCAL_AUTH_STORAGE_KEY)
  } catch (error) {
    console.warn('Unable to remove auth user from local storage', error)
  }
}

function loadPersistedUser() {
  try {
    const raw = localStorage.getItem(LOCAL_AUTH_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    console.warn('Unable to read auth user from local storage', error)
    return null
  }
}

function resolveConfiguredUser() {
  if (window.config && window.config.defaultUser) {
    return window.config.defaultUser
  }

  return {
    email: 'local-user@example.com',
    role: 'admin',
    displayName: 'Local Administrator',
  }
}

function resolveExpectedSecret() {
  if (window.config && window.config.localApiKey) {
    return window.config.localApiKey
  }

  return null
}

function shouldBypassAuth() {
  return Boolean(window.config && window.config.bypassAuth)
}

function shouldClearAuthOnLoad() {
  if (!(window.config && window.config.clearAuthOnLoad)) {
    return false
  }

  try {
    return sessionStorage.getItem(CLEAR_AUTH_ON_LOAD_KEY) !== 'true'
  } catch (error) {
    // Do not force logout when storage is unavailable.
    return false
  }
}

function markClearAuthOnLoadApplied() {
  try {
    sessionStorage.setItem(CLEAR_AUTH_ON_LOAD_KEY, 'true')
  } catch (error) {
    // Ignore session storage write errors.
  }
}

function handleLoginSuccess({ dispatch, getState, user }) {
  dispatch({ type: LOGIN_SUCCESS, user })

  if (isRolesEnabled(getState())) {
    api.getUser().then((spiceUser = {}) => {
      dispatch({ type: LOGIN_SUCCESS, user: { ...user, ...spiceUser } })
    }).catch(() => {
      dispatch({ type: LOGIN_SUCCESS, user })
    })
  }
}

export const LOGIN_PENDING = 'LOGIN_PENDING'
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
export const LOGIN_ERROR = 'LOGIN_ERROR'

export function loginApiKey(secret) {
  return (dispatch, getState) => {
    dispatch({ type: LOGIN_PENDING })

    const expectedSecret = resolveExpectedSecret()
    if (expectedSecret && secret !== expectedSecret) {
      dispatch({ type: LOGIN_ERROR, error: 'Invalid credential' })
      return
    }

    if (!secret && expectedSecret) {
      dispatch({ type: LOGIN_ERROR, error: 'Credential required' })
      return
    }

    const baseUser = {
      ...resolveConfiguredUser(),
      apiKey: secret || expectedSecret || 'local',
    }

    persistUser(baseUser)
    clearForceReauth()
    updateLastActivity()
    handleLoginSuccess({ dispatch, getState, user: baseUser })
  }
}

export function loginAD() {
  return (dispatch, getState) => {
    dispatch({ type: LOGIN_PENDING })

    if (!isEntraConfigured()) {
      dispatch({ type: LOGIN_ERROR, error: 'Entra ID is not configured' })
      return
    }

    const msal = getMsalInstance()
    if (!msal) {
      dispatch({ type: LOGIN_ERROR, error: 'Entra ID client not available' })
      return
    }

    msal.loginPopup(getLoginRequest())
      .then((result) => {
        const account = result && result.account
        if (account) {
          msal.setActiveAccount(account)
        }
        const baseUser = {
          email: account && account.username,
          displayName: account && account.name,
          role: 'user',
          provider: 'entra',
        }
        persistUser(baseUser)
        clearForceReauth()
        updateLastActivity()
        handleLoginSuccess({ dispatch, getState, user: baseUser })
      })
      .catch((error) => {
        dispatch({ type: LOGIN_ERROR, error: error && error.message ? error.message : 'Login failed' })
      })
  }
}

export function checkLogin() {
  return (dispatch, getState) => {
    dispatch({ type: LOGIN_PENDING })

    if (shouldClearAuthOnLoad()) {
      removePersistedUser()

      const msal = getMsalInstance()
      if (msal) {
        msal.setActiveAccount(null)
      }

      markClearAuthOnLoadApplied()
      dispatch({ type: LOGIN_ERROR, error: null })
      return
    }

    if (shouldBypassAuth()) {
      const user = resolveConfiguredUser()
      persistUser(user)
      clearForceReauth()
      handleLoginSuccess({ dispatch, getState, user })
      return
    }

    if (shouldForceReauth()) {
      removePersistedUser()
      dispatch({ type: LOGIN_ERROR, error: null })
      return
    }

    if (isSessionExpired()) {
      removePersistedUser()
      markForceReauth()
      dispatch({ type: LOGIN_ERROR, error: null })
      return
    }

    const persistedUser = loadPersistedUser()
    const account = getActiveAccount()

    if (isEntraConfigured() && account) {
      const user = {
        email: account.username,
        displayName: account.name,
        role: persistedUser && persistedUser.role ? persistedUser.role : 'user',
        provider: 'entra',
      }
      persistUser(user)
      handleLoginSuccess({ dispatch, getState, user })
      return
    }

    if (persistedUser && persistedUser.provider === 'entra' && isEntraConfigured()) {
      removePersistedUser()
      dispatch({ type: LOGIN_ERROR, error: null })
      return
    }

    if (persistedUser) {
      handleLoginSuccess({ dispatch, getState, user: persistedUser })
      return
    }

    if (account) {
      const user = {
        email: account.username,
        displayName: account.name,
        role: 'user',
        provider: 'entra',
      }
      persistUser(user)
      handleLoginSuccess({ dispatch, getState, user })
    } else {
      dispatch({ type: LOGIN_ERROR, error: null })
    }
  }
}

export const LOGOUT_PENDING = 'LOGOUT_PENDING'
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS'

export function logout() {
  return (dispatch) => {
    dispatch({ type: LOGOUT_PENDING })
    removePersistedUser()
    markForceReauth()
    try {
      localStorage.removeItem(LAST_ACTIVITY_KEY)
    } catch (error) {
      // ignore
    }

    const msal = getMsalInstance()
    if (msal) {
      const account = getActiveAccount()
      if (account) {
        msal.logoutPopup({ account })
      }

      msal.setActiveAccount(null)
    }

    dispatch({ type: LOGOUT_SUCCESS })
  }
}

export function checkIdleTimeout() {
  return (dispatch) => {
    if (!isSessionExpired()) return
    removePersistedUser()
    markForceReauth()
    try {
      localStorage.removeItem(LAST_ACTIVITY_KEY)
    } catch (error) {
      // ignore
    }
    const msal = getMsalInstance()
    if (msal) {
      msal.setActiveAccount(null)
    }
    dispatch({ type: LOGOUT_SUCCESS })
  }
}